import { useEffect, useState } from "react";
import { api, formatApiError } from "@/lib/apiClient";
import { toast } from "sonner";
import { DoorOpen, ChevronLeft, ChevronRight, Plus, Check, X, Trash2, Pencil } from "lucide-react";
import { BookingForm, RoomsEditor } from "@/components/RoomBookingForm";
import { BookingEdit } from "@/components/BookingEdit";

const DAYS = ["Seg", "Ter", "Qua", "Qui", "Sex", "Sáb", "Dom"];
const iso = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
const monday = (d) => { const x = new Date(d); x.setDate(x.getDate() - ((x.getDay() + 6) % 7)); x.setHours(0, 0, 0, 0); return x; };

function PendingRow({ b, onDecide }) {
  const [reason, setReason] = useState("");
  const [asking, setAsking] = useState(false);
  return (
    <div className="flex flex-wrap items-center gap-3 py-2 border-b border-[#161616] text-sm" data-testid={`rooms-pending-${b.id}`}>
      <span className="font-bold">{b.room_name}</span>
      <span>{b.date} · {b.start}–{b.end}</span>
      <span className="text-[#A3A3A3] flex-1 truncate">{b.title} · {b.user_name}{b.series_id ? " · recorrente" : ""}</span>
      {asking ? (
        <>
          <input value={reason} onChange={(e) => setReason(e.target.value)} placeholder="Motivo da recusa (vai no email)"
            className="flex-1 min-w-[200px] bg-[#0B0B0B] border border-[#1F1F1F] text-xs px-2 py-1" data-testid={`rooms-reject-reason-${b.id}`} />
          <button onClick={() => onDecide(b, false, reason)} className="px-2 py-1 border border-[#FF6B6B] text-[#FF6B6B] text-[10px] uppercase font-bold"
            data-testid={`rooms-reject-send-${b.id}`}>Enviar recusa</button>
          <button onClick={() => setAsking(false)} className="text-[10px] uppercase text-[#737373]">Cancelar</button>
        </>
      ) : (
        <>
          <button onClick={() => onDecide(b, true)} className="px-2 py-1 border border-[#22C55E] text-[#22C55E] text-[10px] uppercase font-bold inline-flex items-center gap-1"
            data-testid={`rooms-approve-${b.id}`}><Check className="w-3 h-3" /> Aprovar</button>
          <button onClick={() => setAsking(true)} className="px-2 py-1 border border-[#FF6B6B] text-[#FF6B6B] text-[10px] uppercase font-bold inline-flex items-center gap-1"
            data-testid={`rooms-reject-${b.id}`}><X className="w-3 h-3" /> Recusar</button>
        </>
      )}
    </div>
  );
}

export default function RoomBooking() {
  const [week, setWeek] = useState(monday(new Date()));
  const [rooms, setRooms] = useState([]);
  const [data, setData] = useState(null);
  const [form, setForm] = useState(null);
  const [drafts, setDrafts] = useState([]);
  const [editing, setEditing] = useState(null);
  const days = [...Array(7)].map((_, i) => { const d = new Date(week); d.setDate(d.getDate() + i); return d; });

  const load = () => {
    api.get("/rooms").then((r) => setRooms(r.data));
    api.get("/room-drafts").then((r) => setDrafts(r.data)).catch(() => {});
    api.get(`/room-bookings?start=${iso(days[0])}&end=${iso(days[6])}`).then((r) => setData(r.data))
      .catch((e) => toast.error(formatApiError(e)));
  };
  useEffect(load, [week]); // eslint-disable-line react-hooks/exhaustive-deps

  const shift = (n) => { const d = new Date(week); d.setDate(d.getDate() + 7 * n); setWeek(d); };
  const decide = async (b, approve, reason) => {
    try {
      await api.post(`/room-bookings/${b.id}/decision`, { approve, reason: reason || null });
      toast.success(approve ? "Aprovado · o pedinte foi avisado por email" : "Recusado · o pedinte foi avisado por email");
      load();
    } catch (err) { toast.error(formatApiError(err)); }
  };
  const cancel = async (b) => {
    const series = b.series_id && window.confirm("Cancelar também as semanas seguintes desta marcação recorrente?");
    if (!series && !window.confirm(`Cancelar "${b.title}" a ${b.date}?`)) return;
    try { await api.delete(`/room-bookings/${b.id}?series=${!!series}`); load(); } catch (err) { toast.error(formatApiError(err)); }
  };

  return (
    <div className="space-y-8 kw-fade-up" data-testid="rooms-page">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <div className="kw-overline mb-2">Espaços KW Lead</div>
          <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl font-black uppercase tracking-tighter">
            Marcação de <span className="text-[#E50000]">salas</span>
          </h1>
          <p className="text-sm text-[#A3A3A3] mt-2">Pede uma sala; a aprovação é dada por um administrador e recebes a confirmação com o botão para o calendário.</p>
        </div>
        <button onClick={() => setForm({ date: iso(new Date()) })} className="kw-btn-primary inline-flex items-center gap-2" data-testid="rooms-new">
          <Plus className="w-4 h-4" /> Pedir sala
        </button>
      </div>

      {drafts.length > 0 && (
        <div className="kw-card p-5" data-testid="rooms-drafts">
          <div className="kw-overline mb-3">Os meus rascunhos ({drafts.length})</div>
          {drafts.map((d) => (
            <div key={d.id} className="flex flex-wrap items-center gap-3 py-2 border-b border-[#161616] text-sm">
              <span className="flex-1 truncate">{d.title || "(sem motivo)"} · {d.date} {d.start}–{d.end}</span>
              <button onClick={() => setForm(d)} className="kw-btn-secondary !py-1 !px-2 text-[10px]" data-testid={`rooms-draft-open-${d.id}`}>Abrir e enviar</button>
              <button onClick={async () => { await api.delete(`/room-drafts/${d.id}`); load(); }} className="text-[#FF6B6B]" title="Apagar"><Trash2 className="w-4 h-4" /></button>
            </div>
          ))}
        </div>
      )}

      {data?.can_approve && data.pending.length > 0 && (
        <div className="kw-card p-5 border-l-4 !border-l-[#EAB308]" data-testid="rooms-pending">
          <div className="kw-overline mb-3">Pedidos por aprovar ({data.pending.length})</div>
          {data.pending.map((b) => <PendingRow key={b.id} b={b} onDecide={decide} />)}
        </div>
      )}

      <div className="kw-card p-5 overflow-x-auto">
        <div className="flex items-center gap-3 mb-4">
          <button onClick={() => shift(-1)} data-testid="rooms-prev"><ChevronLeft className="w-5 h-5" /></button>
          <span className="font-display font-black uppercase">{days[0].toLocaleDateString("pt-PT")} – {days[6].toLocaleDateString("pt-PT")}</span>
          <button onClick={() => shift(1)} data-testid="rooms-next"><ChevronRight className="w-5 h-5" /></button>
        </div>
        <table className="w-full text-xs min-w-[900px]">
          <thead><tr><th className="text-left p-2 w-36 kw-overline">Sala</th>
            {days.map((d, i) => <th key={i} className="p-2 kw-overline text-left">{DAYS[i]} {d.getDate()}/{d.getMonth() + 1}</th>)}</tr></thead>
          <tbody>
            {rooms.map((r) => (
              <tr key={r.id} className="border-t border-[#1a1a1a] align-top">
                <td className="p-2"><div className="font-bold flex items-center gap-1"><DoorOpen className="w-3.5 h-3.5 text-[#E50000]" />{r.name}</div>
                  <div className="text-[10px] text-[#737373]">{r.capacity} lugares</div></td>
                {days.map((d) => {
                  const list = (data?.bookings || []).filter((b) => b.room_id === r.id && b.date === iso(d));
                  return (
                    <td key={iso(d)} className="p-1.5 cursor-pointer hover:bg-[#111]" onClick={() => setForm({ date: iso(d), room_id: r.id })}
                      data-testid={`rooms-cell-${r.id}-${iso(d)}`}>
                      {list.map((b) => (
                        <div key={b.id} onClick={(e) => e.stopPropagation()}
                          className={`mb-1 px-1.5 py-1 border-l-2 ${b.status === "approved" ? "border-[#22C55E] bg-[#22C55E]/10" : "border-[#EAB308] bg-[#EAB308]/10"}`}>
                          <div className="font-bold">{b.start}–{b.end}</div>
                          <div className="truncate text-[#A3A3A3]" title={`${b.title} · ${b.user_name}`}>{b.title}</div>
                          {b.status === "pending" && <div className="text-[9px] uppercase text-[#EAB308]">por aprovar</div>}
                          {(b.mine || data?.can_manage) && (
                            <div className="flex items-center gap-2 mt-0.5">
                              <button onClick={() => setEditing(b)} className="text-[#A3A3A3] hover:text-white" title="Editar dia, hora ou sala" data-testid={`rooms-edit-${b.id}`}><Pencil className="w-3 h-3" /></button>
                              <button onClick={() => cancel(b)} className="text-[#FF6B6B]" title="Cancelar" data-testid={`rooms-cancel-${b.id}`}><Trash2 className="w-3 h-3" /></button>
                            </div>
                          )}
                        </div>
                      ))}
                    </td>
                  );
                })}
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {data?.can_edit_rooms && <RoomsEditor rooms={rooms} onChange={load} />}
      {editing && <BookingEdit booking={editing} rooms={rooms} onClose={() => setEditing(null)} onDone={() => { setEditing(null); load(); }} />}
      {form && <BookingForm rooms={rooms} initial={form} onClose={() => setForm(null)} onDone={() => { setForm(null); load(); }} />}
    </div>
  );
}
